import i18n from 'i18next';

import { DataBaseNodes } from '@lib/firebase';
import { usePatientStore, useSiteStore } from '@stores';
import { SourceUnsubscribe } from '@type';
import { getDateUTCOffset, getPlatform, logError } from '@utils';

import { PushNotificationsService } from '../../push-notifications-service';
import { Preferences } from '../types';
import { BaseGateway } from './base.gateway';
import { GatewayUtils } from './utils';

export class DeviceGateway extends BaseGateway {
  readonly setDeviceIdentifier = async (uuid: string): Promise<void> => {
    return await this.sourceService.updateData<string>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.DeviceIdentifier}`,
      uuid
    );
  };

  readonly isNewDevice = async (): Promise<boolean> => {
    const { patientInfo } = usePatientStore.getState();
    const deviceIdentifier = await this.sourceService.readDataOnce<string>(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.DeviceIdentifier}`
    );

    return (
      (deviceIdentifier === null && patientInfo.deviceIdentifier !== null) ||
      patientInfo.deviceIdentifier !== deviceIdentifier
    );
  };

  readonly subscribeToDeviceIdentifierChange = (
    callback: () => unknown
  ): SourceUnsubscribe => {
    return this.sourceService.subscribeToChild(
      `${DataBaseNodes.Patients}/${this.config.patientSourceUUID}/${DataBaseNodes.DeviceIdentifier}`,
      snapshot => {
        if (!snapshot.exists()) return;
        const deviceId = snapshot.val() as string;
        const { deviceIdentifier } = usePatientStore.getState().patientInfo;
        if (deviceId !== deviceIdentifier) callback();
      }
    );
  };

  private readonly sendDeviceOSToServer = async (deviceOS: string | null) => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { siteUuid } = getSiteByProjectId(this.config.projectId!);

    const payload = {
      deviceOS,
      type: DataBaseNodes.DeviceOS,
      patientId: this.config.patientSourceUUID,
    };

    await this.sendRequest(siteUuid, payload, DataBaseNodes.DeviceOS);
  };

  readonly syncDeviceOS = async () => {
    const deviceOS = getPlatform();

    await this.sendDeviceOSToServer(deviceOS);
  };

  readonly clearDeviceOS = async () => {
    await this.sendDeviceOSToServer(null);
  };

  private readonly sendDeviceTokenAndLangToServer = async (
    token?: string,
    lang?: string
  ) => {
    const { getSiteByProjectId } = useSiteStore.getState();
    const { siteUuid } = getSiteByProjectId(this.config.projectId!);
    const langPart = lang ?? '';
    const tokenPart = token ? `:${token}` : '';

    const tokenAndLang = !token && !lang ? null : `${langPart}${tokenPart}`;

    const payload = {
      deviceToken: tokenAndLang,
      type: DataBaseNodes.DeviceToken,
      patientId: this.config.patientSourceUUID,
      deviceOffset: getDateUTCOffset(),
      appVersion: import.meta.env.APP_VERSION,
    };

    await this.sendRequest(siteUuid, payload, DataBaseNodes.DeviceToken);
  };

  private readonly getDeviceToken = async () => {
    try {
      const token = await PushNotificationsService.getToken();
      return token;
    } catch (error) {
      logError(`Failed to get push notification token error=${String(error)}`);
      return undefined;
    }
  };

  private readonly getDeviceLang = () => {
    const deviceLanguage = GatewayUtils.mapLangsToMYCLangs(i18n.language);
    return deviceLanguage;
  };

  readonly syncDeviceToken = async (token?: string) => {
    const lang = this.getDeviceLang();

    await this.sendDeviceTokenAndLangToServer(token, lang);
  };

  readonly syncDeviceLang = async () => {
    const lang = this.getDeviceLang();
    const token = await this.getDeviceToken();
    await this.sendDeviceTokenAndLangToServer(token, lang);
  };

  readonly clearDeviceToken = async () => {
    const lang = this.getDeviceLang();
    await this.sendDeviceTokenAndLangToServer(undefined, lang);
  };

  readonly clearDeviceTokenAndLang = async () => {
    await this.sendDeviceTokenAndLangToServer(undefined, undefined);
  };

  readonly markNotificationsRead = async () => {
    try {
      const { getSiteByProjectId } = useSiteStore.getState();
      const site = getSiteByProjectId(this.config.projectId!);

      const payload = {
        type: Preferences.Device.ResetNotifications,
        preference: Preferences.Device.ResetNotifications,
        firebasePatientId: this.config.patientSourceUUID,
      };

      await this.sendRequest(
        site.siteUuid,
        payload,
        DataBaseNodes.Request,
        this.config.patientSourceUUID
      );
    } catch (error) {
      logError(`Failed to mark notifications read error:${String(error)}`);
    }
  };
}
